import { useParams } from "react-router-dom";
import { SmoothLink as Link } from "@/components/navigation";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Calendar, Clock, ArrowLeft, User, Share2, BookOpen, MessageCircle } from "lucide-react";
import { format } from "date-fns";
import { ArticleMeta } from "@/components/seo/ArticleMeta";
import { ArticleJsonLd } from "@/components/seo/ArticleJsonLd";
import { BreadcrumbJsonLd } from "@/components/seo";
import { Breadcrumbs } from "@/components/navigation/Breadcrumbs";
import { RelatedContent } from "@/components/blog/RelatedContent";
import { useBlogPost } from "@/hooks/useBlogPosts";
import { LazyImage } from "@/components/common/LazyImage";
import { getOptimizedImageUrl } from "@/lib/image-utils";
import { AIAgentCard } from "@/components/ai";
import { getWhatsAppLink, COMPANY } from "@/config/company";
import { PageHeader } from "@/components/shared/PageHeader";
import { SEOHead } from "@/components/seo";

// Safe link renderer to prevent XSS
function SafeLink({ href, children }: { href?: string; children: React.ReactNode }) {
  const isSafeUrl = href && !/^(javascript|data):/i.test(href);
  return (
    <a 
      href={isSafeUrl ? href : '#'} 
      className="text-primary hover:underline font-medium"
      rel="noopener noreferrer"
      target={href?.startsWith('http') ? '_blank' : undefined}
    >
      {children}
    </a>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const { data: post, isLoading } = useBlogPost(slug);

  const canonicalUrl = `${COMPANY.website}/blog/${slug}/`;
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/blog/${slug}/`
    : canonicalUrl;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
      <SEOHead
        title={post?.title || "Blog Post"}
        description={post?.excerpt || "Read our latest medical tourism insights"}
        canonicalPath={`/blog/${slug}/`}
        ogType="article"
        ogImage={post?.featured_image}
      />


        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded w-3/4 mb-4" />
              <div className="h-4 bg-muted rounded w-1/2 mb-8" />
              <div className="h-64 bg-muted rounded mb-8" />
              <div className="space-y-4">
                <div className="h-4 bg-muted rounded" />
                <div className="h-4 bg-muted rounded" />
                <div className="h-4 bg-muted rounded w-2/3" />
              </div>
            </div>
          </div>
        </main>

      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">

        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-display text-4xl font-bold text-secondary mb-4">
              Article Coming Soon
            </h1>
            <p className="text-muted-foreground mb-8">
              This article is being prepared by our medical experts. Browse our other insightful health articles!
            </p>
            <Link to="/blog/">
              <Button className="rounded-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Explore Articles
              </Button>
            </Link>
          </div>
        </main>

      </div>
    );
  }

  const metaDescription = post.meta_description || post.excerpt || post.title;
  const metaTitle = post.meta_title || post.title;

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags */}
      <ArticleMeta
        title={metaTitle}
        description={metaDescription}
        author={post.author || "TrustedHeal"}
        publishedAt={post.published_at || new Date().toISOString()}
        imageUrl={getOptimizedImageUrl(post.hero_image_url, 1200) || undefined}
        url={canonicalUrl}
        keywords={post.focus_keywords || post.tags || []}
        canonicalUrl={post.canonical_url || undefined}
      />
      
      {/* JSON-LD Structured Data */}
      <ArticleJsonLd
        title={metaTitle}
        description={metaDescription}
        author={post.author || "TrustedHeal"}
        publishedAt={post.published_at || new Date().toISOString()}
        imageUrl={getOptimizedImageUrl(post.hero_image_url, 1200) || undefined}
        url={canonicalUrl}
        keywords={post.focus_keywords || post.tags || []}
      />

      {/* Breadcrumb Schema */}
      <BreadcrumbJsonLd items={[
        { name: "Home", url: `${COMPANY.website}/` },
        { name: "Blog", url: `${COMPANY.website}/blog/` },
        { name: post.title, url: canonicalUrl }
      ]} />

      <main className="pt-24 pb-16">
        <article className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb Navigation */}
          <Breadcrumbs items={[
            { name: "Home", url: "/" },
            { name: "Blog", url: "/blog/" },
            { name: post.title, url: `/blog/${slug}/` }
          ]} />

          {/* Header */}
          <PageHeader 
            title={post.title}
            badge={<Badge className="mb-4">{post.category}</Badge>}
            className="mb-8"
          >
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground mt-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              {post.published_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <time dateTime={post.published_at}>
                    {format(new Date(post.published_at), "MMMM d, yyyy")}
                  </time>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.read_time_minutes} min read</span>
              </div>
            </div>
          </PageHeader>

          {/* Featured Image with Lazy Loading */}
          <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
            <LazyImage
              src={getOptimizedImageUrl(post.hero_image_url, 1200) || ""}
              alt={post.title}
              width={1200}
              height={675}
              className="h-64 md:h-96"
              aspectRatio="auto"
              priority
              fallbackIcon={<BookOpen className="w-24 h-24 text-primary-foreground/30" />}
              fallbackClassName="bg-gradient-to-br from-primary to-accent"
            />
          </div>

          {/* Content with Typography Plugin */}
          <div className="prose prose-lg prose-slate dark:prose-invert max-w-none
            prose-headings:font-display prose-headings:text-secondary
            prose-h1:text-3xl prose-h1:mt-8 prose-h1:mb-4
            prose-h2:text-2xl prose-h2:mt-6 prose-h2:mb-3
            prose-h3:text-xl prose-h3:mt-4 prose-h3:mb-2
            prose-p:text-foreground prose-p:leading-relaxed
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground
            prose-strong:text-secondary prose-strong:font-semibold
            prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
            prose-pre:bg-muted prose-pre:rounded-lg
            prose-table:border prose-table:border-border
            prose-th:bg-muted prose-th:p-3 prose-th:text-left prose-th:font-semibold
            prose-td:p-3 prose-td:border-t prose-td:border-border
          ">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => <SafeLink href={href}>{children}</SafeLink>,
                // Map markdown h1 -> h2 to prevent duplicate H1 with PageHeader
                h1: ({ children }) => <h2 className="font-display text-2xl font-bold text-secondary mt-8 mb-4">{children}</h2>,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-8 pt-8 border-t border-border">
              <h3 className="font-semibold text-secondary mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <Badge key={tag} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="mt-8 pt-8 border-t border-border">
            <div className="flex items-center gap-4">
              <span className="font-semibold text-secondary">Share this article:</span>
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-full"
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: post.title,
                      text: metaDescription,
                      url: shareUrl,
                    });
                  } else {
                    navigator.clipboard.writeText(shareUrl);
                  }
                }}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>

          {/* Related Content Section */}
          <RelatedContent
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            treatmentCategories={(post.related_treatment_categories || []) as any}
            treatmentIds={post.related_treatment_ids || []}
            hospitalIds={post.related_hospital_ids || []}
            doctorIds={post.related_doctor_ids || []}
          />

          {/* Context-Aware AI Assistant - Conversion Focused CTA */}
          <div className="mt-12 space-y-4">
            <AIAgentCard 
              context={post.title}
              title="Talk to Dr. Maya About This"
              description={`Ready to explore ${post.category || 'treatment'} options? Dr. Maya can answer your questions and help you take the next step.`}
              quickPrompts={[
                "Get a free consultation",
                "What will this cost me?",
                "Am I a good candidate?"
              ]}
            />
            
            {/* Alternative Contact CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 p-4 bg-muted/50 rounded-xl border border-border">
              <p className="text-sm text-muted-foreground text-center sm:text-left">
                Prefer to speak with a human? Our care coordinators are here to help.
              </p>
              <div className="flex items-center gap-2">
                <Button asChild variant="outline" size="sm" className="rounded-full">
                  <Link to="/contact/">Contact Us</Link>
                </Button>
                <Button 
                  asChild 
                  size="sm" 
                  className="rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white gap-1.5"
                >
                  <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </article>
      </main>

    </div>
  );
}
